import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Form, Input, Switch, Tooltip } from "antd";
import { GET_SUPPLIER_CURSOR } from "graphql/query/supplier-gql";
import { GET_ORGANIZERS_CURSOR } from "graphql/query/organizer-gql";
import { exhibitionUseMutation } from "../../hooks";
import { Localize, EntityRemoveButton } from "components/service";
import { localizeByKey, localizeByKeyStatic } from "components/service/localize";
import type { TExhibitionType } from "components/exhibition/helpers/exhibition-types-helper";
import { EExhibitionStatus } from "components/exhibition/helpers/exhibition-types-helper";
import type { TOrganizer } from "components/organizer/helpers/organizer-types-helper";
import { TSupplier } from "components/supplier/helpers/supplier-types-helper";
import { FormElements } from "components/layout";
import Icons from "components/icons";
import { PermissionButton, useModelFormValidation, usePermissionByObject } from "components/permissions";
import Exhibitions from "components/exhibition";
import ExhibitionFormItems from "../fields";

interface IExhibitionEditFormProps
{
    exhibition?: TExhibitionType | undefined;
    organizer?: TOrganizer | undefined;
}

const ExhibitionEditForm: React.FC<IExhibitionEditFormProps> = ({ exhibition, organizer }) =>
{
    const [ form ] = Form.useForm();
    const [ statusValue, setStatusValue ] = useState(exhibition?.status === EExhibitionStatus.ACTIVE);

    const {
        _setExhibitionCreateUpdate,
        loadingMutationExhibitionCreateUpdate: loading,
    } = exhibitionUseMutation.createUpdate(exhibition?.id, organizer);

    const { _setMutationExhibitionDelete, loadingMutationExhibitionDelete } =
        exhibitionUseMutation.delete(exhibition?.id, organizer);

    const { validateUpdatePermission, validateDeletePermission } = useModelFormValidation(Exhibitions.Const);
    const { hasPermission: hasChangeStatusPermission } = usePermissionByObject(Exhibitions.Const.permissions.CHANGE_STATUS);
    const { hasPermission: hasUpdatePermission } = usePermissionByObject(Exhibitions.Const.permissions.UPDATE);

    const getInitialValueBySupplierGroup = (exhibition: TExhibitionType | undefined, supplierGroup: string) =>
    {
        if (!exhibition) {
            return undefined;
        }

        const suppliersInGroup = exhibition.suppliers.filter(
            (supplier: TSupplier) => supplier.supplier_group === supplierGroup
        );

        return suppliersInGroup.length > 0
            ? suppliersInGroup.map(({ supplier_id, supplier_title }) => ({
                value: supplier_id,
                label: supplier_title,
            }))
            : undefined;
    };

    const valueFieldStatus = Form.useWatch("status", form);

    const getInitialValueByOrganizer = {
        exhibition: exhibition?.organizer?.id
            ? [
                {
                    value: exhibition?.organizer?.id,
                    label: exhibition?.organizer?.title,
                },
            ]
            : undefined,
        organizer: organizer?.id
            ? [
                {
                    value: organizer?.id,
                    label: organizer?.title,
                },
            ]
            : undefined,
    };

    const exhibitionTypeSuppliers = (group: string) =>
    {
        if (exhibition?.default) {
            return undefined;
        } else {
            return {
                whereExhibitionTypeSuppliers: {
                    column: "ET_ID",
                    operator: "EQ",
                    value:
                        organizer?.et_default_id ||
                        exhibition?.organizer?.et_default_id,
                    AND: [
                        {
                            column: "SUPPLIER_GROUP",
                            operator: "EQ",
                            value: group,
                        },
                    ],
                },
            };
        }
    };

    return (
        <Form
            key="edit-exhibition-form"
            layout="vertical"
            form={form}
            className="model-form edit-exhibition-form"
            onFinish={(values) =>
            {
                if (!validateUpdatePermission()) {
                    return;
                }

                const selectedSuppliers: { supplier_group: string, supplier_id: string }[] = [];
                const groups = [ "equipment", "print_house", "flooring" ];
                const processGroup = (group: string) =>
                {
                    if (values[ `suppliers-${group}` ]) {
                        selectedSuppliers.push(
                            ...values[ `suppliers-${group}` ].map((supplier: string) => ({
                                supplier_group: group,
                                supplier_id: supplier,
                            }))
                        );
                    }
                };

                groups.forEach(processGroup);

                _setExhibitionCreateUpdate({
                    variables: {
                        input: {
                            id: exhibition?.id ? +exhibition.id : undefined,
                            title: values.title,
                            organizer_id: values.organizer_id,
                            suppliers: selectedSuppliers,
                            import_template: values.exhibition_type,
                            status:
                                typeof values.status === "string"
                                    ? exhibition?.status
                                    : values?.status === true
                                        ? EExhibitionStatus.ACTIVE
                                        : EExhibitionStatus.CLOSED,
                        },
                    },
                });
            }}
        >
            <div className="switch-holder">
                <Form.Item
                    name="status"
                    label={<Localize>FORMS.Input_Label_Status</Localize>}
                    initialValue={
                        exhibition?.status === EExhibitionStatus.ACTIVE
                    }
                    valuePropName="checked"
                    className="form-item-switch"
                >
                    <Tooltip
                        title={!hasChangeStatusPermission ? `${localizeByKeyStatic('PERMISSION.Error_AccessDenied')}: ${localizeByKeyStatic(Exhibitions.Const.permissions.CHANGE_STATUS.slug)}` : undefined}
                    >
                        <Switch
                            disabled={
                                (!hasChangeStatusPermission) ||
                                (exhibition?.default &&
                                    exhibition?.status === EExhibitionStatus.ACTIVE)
                            }
                            checked={statusValue}
                            onChange={(checked) =>
                            {
                                setStatusValue(checked);
                                form.setFieldValue('status', checked);
                            }}
                        />
                    </Tooltip>
                </Form.Item>
                {valueFieldStatus ? (
                    <span
                        style={{ color: "var(--switch_colorPrimary)" }}
                        className="switch-status-text"
                    >
                        <Localize>GLOBAL.Text_Active</Localize>
                    </span>
                ) : (
                    <span
                        style={{ color: "var(--switch_colorTextQuaternary)" }}
                        className="switch-status-text"
                    >
                        <Localize>GLOBAL.Text_Blocked</Localize>
                    </span>
                )}
            </div>

            <div className="row-grid grid-gap-10 row-gap-0 col-md-2">
                <Form.Item
                    name="title"
                    label={
                        <Localize>FORMS.Input_Label_ExhibitionName</Localize>
                    }
                    initialValue={exhibition?.title}
                    rules={[
                        {
                            required: true,
                            message: (
                                <Localize>
                                    FORM_RULES.Required_ExhibitionName
                                </Localize>
                            ),
                        },
                    ]}
                >
                    <Input name="title" />
                </Form.Item>

                <ExhibitionFormItems.ExhibitionTypeSelect
                    name="exhibition_type"
                    label="FORMS.Input_Label_ExhibitionTypes"
                    initialValue={exhibition?.import_template}
                />

                <FormElements.RadioSelect
                    disabled={!!(exhibition?.id || organizer?.id)}
                    form={form}
                    query={GET_ORGANIZERS_CURSOR}
                    model="organizersCursor"
                    name="organizer_id"
                    label="FORMS.Input_Label_Organizer"
                    modalTitle="FORMS.Title_AddOrganizer"
                    initialValue={
                        getInitialValueByOrganizer[
                        exhibition ? "exhibition" : "organizer"
                        ]
                    }
                    tagInfo={!!exhibition && (
                        <Link
                            className="select-link"
                            to="#"
                        >
                            <Icons.Information />
                        </Link>
                    )}
                />
            </div>

            {(organizer || exhibition) && (
                <>
                    <FormElements.CheckboxSelect
                        form={form}
                        query={GET_SUPPLIER_CURSOR}
                        model="suppliersCursor"
                        name="suppliers-equipment"
                        label="FORMS.Input_Label_EquipmentSuppliers"
                        modalTitle="FORMS.Title_AddEquipmentSuppliers"
                        modalButtonText="MODAL.Button_Text_AddSupplier"
                        extraVariables={exhibitionTypeSuppliers("equipment")}
                        initialValue={getInitialValueBySupplierGroup(
                            exhibition,
                            "equipment"
                        )}
                        skipAddButton={!hasUpdatePermission}
                        disabledTags={!hasUpdatePermission ? (getInitialValueBySupplierGroup(exhibition, "equipment")?.map(item => item.value) || []) : []}
                    />

                    <FormElements.CheckboxSelect
                        form={form}
                        query={GET_SUPPLIER_CURSOR}
                        model="suppliersCursor"
                        name="suppliers-print_house"
                        label="FORMS.Input_Label_PrintHouseSuppliers"
                        modalTitle="FORMS.Title_AddPrintHouseSuppliers"
                        modalButtonText="MODAL.Button_Text_AddSupplier"
                        extraVariables={exhibitionTypeSuppliers("print_house")}
                        initialValue={getInitialValueBySupplierGroup(
                            exhibition,
                            "print_house"
                        )}
                        skipAddButton={!hasUpdatePermission}
                        disabledTags={!hasUpdatePermission ? (getInitialValueBySupplierGroup(exhibition, "print_house")?.map(item => item.value) || []) : []}
                    />

                    <FormElements.CheckboxSelect
                        form={form}
                        query={GET_SUPPLIER_CURSOR}
                        model="suppliersCursor"
                        name="suppliers-flooring"
                        label="FORMS.Input_Label_FlooringSuppliers"
                        modalTitle="FORMS.Title_AddFlooringSuppliers"
                        modalButtonText="MODAL.Button_Text_AddSupplier"
                        extraVariables={exhibitionTypeSuppliers("flooring")}
                        initialValue={getInitialValueBySupplierGroup(
                            exhibition,
                            "flooring"
                        )}
                        skipAddButton={!hasUpdatePermission}
                        disabledTags={!hasUpdatePermission ? (getInitialValueBySupplierGroup(exhibition, "flooring")?.map(item => item.value) || []) : []}
                    />
                </>
            )}

            <div className="form-btn-holder">
                {exhibition?.id && (
                    <EntityRemoveButton
                        modalButton={
                            <PermissionButton
                                permission={Exhibitions.Const.permissions.DELETE}
                                disabledFallback={true}
                                icon={<Icons.Delete />}
                            >
                                <span className="ellipsis">
                                    <Localize>
                                        EXHIBITION.Button_Text_Delete
                                    </Localize>
                                </span>
                            </PermissionButton>
                        }
                        nameEntity={
                            localizeByKey("ENTITY.Modal_Title_ExhibitionType")
                        }
                        dataNameEntity={exhibition?.title}
                        loading={loadingMutationExhibitionDelete}
                        deleteMutation={(options) =>
                        {
                            if (!validateDeletePermission()) {
                                return Promise.reject(new Error('Access denied'));
                            }
                            return _setMutationExhibitionDelete(options);
                        }}
                        variables={{
                            id: exhibition?.id,
                        }}
                    />
                )}
                <PermissionButton
                    permission={Exhibitions.Const.permissions.UPDATE}
                    disabledFallback={true}
                    htmlType="submit"
                    className="btn-right"
                    type="primary"
                    icon={exhibition?.id ? <Icons.Edit /> : <Icons.Plus />}
                    loading={loading}
                >
                    <span className="ellipsis">
                        {exhibition?.id ? (
                            <Localize>GLOBAL.Button_Text_SaveChanges</Localize>
                        ) : (
                            <Localize>GLOBAL.Button_Text_CreateNew</Localize>
                        )}
                    </span>
                </PermissionButton>
            </div>
        </Form>
    );
};

export default ExhibitionEditForm;